import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext } from '@providers/userContextProvider';
import { isNull } from 'lodash-es';
import type { ChangeEvent } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';

export type AssetClassSelectItem = {
  id: string;
  key?: string;
  name?: string;
  translatedName?: TranslatedString;
};

export const FETCH_ASSET_CLASSES = gql`
  query fetchAssetClasses($input: FetchAssetClassesInput) {
    fetchAssetClasses(input: $input) {
      assetClasses {
        id
        key
        name
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

export const AssetClassSelect = ({
  setAssetClass,
  onSelectFull,
  value,
  label,
  size,
  disabled = false,
  disabledWith = null,
  filteredAllocations = [],
  omitAny = false,
  leadingIcon,
  required = false,
  testId = 'asset-class-select',
  selectFieldTestId,
}: {
  setAssetClass: (event: ChangeEvent<HTMLInputElement>) => void;
  onSelectFull?: (item: AssetClassSelectItem) => void;
  value: string;
  label: string;
  size?: 'small' | 'medium';
  /** Shows the picked asset class without letting it be changed. */
  disabled?: boolean;
  disabledWith?: string | null;
  filteredAllocations?: string[];
  omitAny?: boolean;
  leadingIcon?: 'dollar' | 'percent' | 'search';
  required?: boolean;
  testId?: string;
  /** Replaces the default select ID, which embeds the translated label. */
  selectFieldTestId?: string;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['configureModels']);
  const canReadAssetClasses = usePermission('read:asset_classes');
  const { activeOrganization } = useContext(UserContext);
  const { loading, data } = useQuery(FETCH_ASSET_CLASSES, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization.id },
      },
    },
  });

  const list: AssetClassSelectItem[] =
    data?.fetchAssetClasses?.assetClasses || [];
  const filteredList =
    filteredAllocations.length === 0
      ? list
      : list.filter(
          (i) => i.id === value || !filteredAllocations.includes(i.id ?? ''),
        );

  const renderMenuItems = () => {
    if (loading) {
      return (
        <MenuItem data-testid={`${testId}-menu-item-loading`}>...</MenuItem>
      );
    }

    if (disabledWith) {
      return (
        <MenuItem
          key={value}
          value={value}
          data-testid={`${testId}-menu-item-disabled-${disabledWith}`}
        >
          <b>{disabledWith}</b>
        </MenuItem>
      );
    }

    return filteredList?.map((x) => (
      <MenuItem
        key={x.id}
        value={x.id}
        data-testid={`${testId}-menu-item-${x.id}`}
      >
        {translateBackendString(x.translatedName)}
      </MenuItem>
    ));
  };

  return (
    <>
      {canReadAssetClasses ? (
        <SelectField
          testId={selectFieldTestId ?? `${testId}-select-field-${label}`}
          leadingIcon={leadingIcon}
          fullWidth
          value={data ? value : ''}
          label={label}
          sx={{ width: '100%' }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setAssetClass(event);
            if (onSelectFull) {
              const selected = filteredList.find(
                (x) => x.id === event.target.value,
              );
              if (selected) onSelectFull(selected);
            }
          }}
          size={size}
          disabled={disabled || !isNull(disabledWith)}
          required={required}
        >
          {!omitAny && (
            <MenuItem data-testid={`${testId}-menu-item-any`} value='ANY'>
              {t('components:any')}
            </MenuItem>
          )}
          {renderMenuItems()}
        </SelectField>
      ) : (
        <></>
      )}
    </>
  );
};

export default AssetClassSelect;
